import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import AdminLayout from './components/AdminLayout';
import AdminLogin from './pages/AdminLogin';
import Dashboard from './pages/dashboard/Dashboard';
import Analytics from './pages/analytics/Analytics';
import Users from './pages/users/Users';
import UserSegments from './pages/users/Segments';
import UserLifecycle from './pages/users/Lifecycle';
import UserBehavior from './pages/users/Behavior';
import Communication from './pages/communication/Communication';
import Splits from './pages/splits/Splits';
import Transactions from './pages/transactions/Transactions';
import Wallets from './pages/wallets/Wallets';
import Badges from './pages/badges/Badges';
import Waitlist from './pages/waitlist/Waitlist';
import Settings from './pages/settings/Settings';
import MetricDetails from './pages/details/MetricDetails';
import ErrorBoundary from './components/ui/ErrorBoundary';
import { useAdminAuth } from './hooks/useAdminAuth';
import { ToastProvider } from './components/ui/Toast';
import { SettingsProvider } from './contexts/SettingsContext';
import { ROUTES } from './constants';
import './styles/AdminStyles.css';

/**
 * Redirect component for metric details with parameter preservation
 */
const MetricDetailsRedirect = () => {
  const { metricId } = useParams();
  return <Navigate to={`${ROUTES.DASHBOARD}/details/${metricId}`} replace />;
};

/**
 * Admin App Component
 * Main application component with routing and authentication
 */
const AdminApp = () => {
  const { isAuthenticated, isLoading } = useAdminAuth();

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin />;
  }

  return (
    <ErrorBoundary>
      <SettingsProvider>
        <AdminLayout>
          <ToastProvider>
            <Routes>
              <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
              {/* Clean routes (work on both admin subdomain and main site via getAdminPrefix) */}
              <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
              <Route path={ROUTES.USERS} element={<Users />} />
              <Route path={ROUTES.USER_SEGMENTS} element={<UserSegments />} />
              <Route path={ROUTES.USER_LIFECYCLE} element={<UserLifecycle />} />
              <Route path={ROUTES.USER_BEHAVIOR} element={<UserBehavior />} />
              <Route path={ROUTES.COMMUNICATION} element={<Communication />} />
              <Route path={ROUTES.SPLITS} element={<Splits />} />
              <Route path={ROUTES.TRANSACTIONS} element={<Transactions />} />
              <Route path={ROUTES.WALLETS} element={<Wallets />} />
              <Route path={ROUTES.BADGES} element={<Badges />} />
              <Route path={ROUTES.ANALYTICS} element={<Analytics />} />
              <Route path={ROUTES.WAITLIST} element={<Waitlist />} />
              <Route path={ROUTES.SETTINGS} element={<Settings />} />
              <Route path={`${ROUTES.DASHBOARD}/details/:metricId`} element={<MetricDetails />} />
              {/* Legacy /admin/* routes redirect to clean routes for backward compatibility */}
              <Route path="/admin/dashboard" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
              <Route path="/admin/users" element={<Navigate to={ROUTES.USERS} replace />} />
              <Route path="/admin/users/segments" element={<Navigate to={ROUTES.USER_SEGMENTS} replace />} />
              <Route path="/admin/users/lifecycle" element={<Navigate to={ROUTES.USER_LIFECYCLE} replace />} />
              <Route path="/admin/users/behavior" element={<Navigate to={ROUTES.USER_BEHAVIOR} replace />} />
              <Route path="/admin/communication" element={<Navigate to={ROUTES.COMMUNICATION} replace />} />
              <Route path="/admin/splits" element={<Navigate to={ROUTES.SPLITS} replace />} />
              <Route path="/admin/transactions" element={<Navigate to={ROUTES.TRANSACTIONS} replace />} />
              <Route path="/admin/wallets" element={<Navigate to={ROUTES.WALLETS} replace />} />
              <Route path="/admin/badges" element={<Navigate to={ROUTES.BADGES} replace />} />
              <Route path="/admin/analytics" element={<Navigate to={ROUTES.ANALYTICS} replace />} />
              <Route path="/admin/waitlist" element={<Navigate to={ROUTES.WAITLIST} replace />} />
              <Route path="/admin/settings" element={<Navigate to={ROUTES.SETTINGS} replace />} />
              <Route path="/admin/dashboard/details/:metricId" element={<MetricDetailsRedirect />} />
              {/* Catch all for unmatched routes */}
              <Route path="/*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
            </Routes>
          </ToastProvider>
        </AdminLayout>
      </SettingsProvider>
    </ErrorBoundary>
  );
};

export default AdminApp;
